import { createBrowserRouter, Navigate } from 'react-router-dom';
import Layout from 'features/layout/Layout';
import ErrorBoundary from 'features/ErrorBoundary';
import Login from 'pages/Login/Login.tsx';
import MFASetupPage from 'pages/MFASetup';
import Admin from 'pages/Admin';
import SettingsPage from 'pages/Settings/SettingsPage.tsx';
import StudyDetail from 'pages/StudyDetail/StudyDetail.tsx';
import ViewerPopup from 'pages/ViewerPopup/ViewerPopup.tsx';
import CopilotPopout from 'pages/CopilotPopout/CopilotPopout.tsx';
import ReportDetail from 'pages/ReportDetail/ReportDetail.tsx';
import Worklist from 'pages/Worklist/Worklist.tsx';
import SearchResults from 'pages/SearchResults/SearchResults.tsx';
import Empty from 'pages/Empty';
import TemplatesPage from 'pages/Templates/TemplatesPage.tsx';
import WorksheetEditorPage from 'pages/Templates/WorksheetEditorPage.tsx';
import PatientDetail from 'pages/PatientDetail/PatientDetail.tsx';
import Reports from 'pages/Reports/Reports.tsx';
import Patients from 'pages/Patients/Patients.tsx';
import Uploads from 'pages/Uploads/Uploads.tsx';
import {
  rootLoader,
  dashboardLoader,
  loginLoader,
  mfaSetupLoader,
} from './loaders';

/**
 * React Router v7 Data Router Configuration
 *
 * Features:
 * - Data loading with loaders
 * - Form handling with actions
 * - Error boundaries for better error handling
 * - Type-safe route definitions
 * - Nested routing with layout
 * - Authentication protection
 */
const routes = createBrowserRouter([
  {
    path: '/login',
    element: <Login />,
    loader: loginLoader,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/mfa-setup',
    element: <MFASetupPage />,
    loader: mfaSetupLoader,
    errorElement: <ErrorBoundary />,
  },
  {
    // Standalone viewer popup - no Layout wrapper
    path: '/studies/:studyUuid/viewer',
    element: <ViewerPopup />,
    loader: rootLoader, // Ensures user is authenticated
    errorElement: <ErrorBoundary />,
  },
  {
    // Standalone copilot popout window - no Layout wrapper
    path: '/copilot/popout',
    element: <CopilotPopout />,
    loader: rootLoader, // Ensures user is authenticated
    errorElement: <ErrorBoundary />,
  },
  {
    id: 'root',
    path: '/',
    element: <Layout />,
    loader: rootLoader,
    // Skip loader revalidation for in-reader navigation (dicom changes AND
    // study-to-study switches): auth was checked on entry, and re-running the
    // async loader would delay the router completing the navigation — the URL
    // and worklist highlight must move in the same instant as the click.
    shouldRevalidate: ({ currentParams, nextParams }) => {
      if (currentParams.studyUuid && nextParams.studyUuid) {
        return false;
      }
      return true;
    },
    errorElement: <ErrorBoundary />,
    children: [
      {
        index: true,
        element: <Navigate to="/studies" replace />,
      },
      {
        path: 'studies',
        element: <StudyDetail />,
      },
      {
        path: 'studies/:studyUuid/:dicomIndex?',
        element: <StudyDetail />,
      },
      {
        path: 'patients',
        element: <Patients />,
      },
      {
        path: 'patients/:patientId',
        element: <PatientDetail />,
      },
      {
        path: 'reports',
        element: <Reports />,
      },
      {
        path: 'reports/:studyUuid',
        element: <Navigate to="preview" replace />,
      },
      {
        path: 'reports/:studyUuid/history',
        element: <ReportDetail />,
        loader: rootLoader,
      },
      {
        path: 'reports/:studyUuid/preview',
        element: <ReportDetail />,
        loader: rootLoader,
      },
      {
        path: 'reports/:studyUuid/attachments',
        element: <ReportDetail />,
        loader: rootLoader,
      },
      {
        path: 'templates',
        element: <TemplatesPage />,
      },
      {
        path: 'templates/worksheets',
        element: <TemplatesPage />,
      },
      {
        path: 'templates/templates',
        element: <TemplatesPage />,
      },
      {
        path: 'templates/worksheets/:uuid',
        element: <WorksheetEditorPage />,
      },
      {
        path: 'uploads',
        element: <Uploads />,
      },
      {
        path: 'worklist',
        element: <Worklist />,
      },
      {
        path: 'admin/*',
        element: <Admin />,
        loader: dashboardLoader,
      },
      {
        path: 'settings',
        element: <SettingsPage />,
        loader: dashboardLoader,
      },
      {
        path: 'search',
        element: <SearchResults />,
      },
      {
        path: 'empty',
        element: <Empty />,
      },
    ],
  },
  {
    path: '*',
    element: <ErrorBoundary />,
  },
]);

export default routes;
